import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Lock, Sparkles, Crown, X, ArrowRight, Zap, Clock, CheckCircle2,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/* ==========================================================================
   Sprint J-3 · PaywallModal
   ==========================================================================
   Opens whenever the backend returns HTTP 402 on a gated action. Two CTAs:
     • "Start free 14-day trial" — only when `trial_available` is true AND the
       feature is workforce_hire (trial is scoped to one Workforce Member).
     • "See pricing plans" — always visible; routes to /app/billing with the
       upgrade_sku_hint highlighted.

   Owner NEVER sees this modal — the backend short-circuits gates for owner.

   Trigger: any code that catches a 402 response can dispatch:
     window.dispatchEvent(new CustomEvent("wfos:paywall", { detail: { ... } }))
   with the parsed 402 body (feature, message, upgrade_sku_hint, trial_available…).
   The api.js response interceptor does this automatically.
   ========================================================================== */

const FEATURE_LABELS = {
    workforce_hire:       "Activate a Workforce Member",
    atlas_message:        "Send another Atlas message",
    skill_run:            "Run a Skill",
    orchestration_run:    "Run an Orchestration",
    workspace_ai_write:   "AI writing in Documents",
    workspace_ai_reply:   "AI email replies",
    workspace_ai_summary: "AI meeting summaries",
    workspace_ai_qa:      "AI Q&A over Knowledge",
};

const REASON_HEADLINES = {
    paywall_required:  "Upgrade to unlock this feature",
    trial_available:   "Try it free for 14 days",
    quota_exceeded:    "You've hit your monthly limit",
    seat_cap_reached:  "You've filled your Workforce Member seats",
    trial_cap_reached: "Your free trial covers one Workforce Member",
};

export default function PaywallModal() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [detail, setDetail] = useState(null);
    const [trialWorker, setTrialWorker] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const onOpen = (e) => {
            setDetail(e.detail || {});
            setTrialWorker(e.detail?.trial_worker_slug || null);
        };
        window.addEventListener("wfos:paywall", onOpen);
        return () => window.removeEventListener("wfos:paywall", onOpen);
    }, []);

    if (!detail) return null;
    // Never gate the owner — belt-and-suspenders (backend already short-circuits).
    if (user?.role === "platform_owner") return null;

    const close = () => setDetail(null);
    const feature = detail.feature || "workforce_hire";
    const reason = detail.reason || "paywall_required";
    const trialAvailable = !!detail.trial_available;
    const trialForHire = trialAvailable && feature === "workforce_hire";
    const upgradeSku = detail.upgrade_sku_hint || "bundle_starter_monthly";
    const headline = REASON_HEADLINES[reason] || REASON_HEADLINES.paywall_required;
    const featureLabel = FEATURE_LABELS[feature] || "this feature";
    const message = detail.message ||
        `${featureLabel} requires a paid plan.`;

    const startTrial = async () => {
        // For workforce_hire the modal-opener is expected to pass a `worker_slug`.
        const slug = detail.worker_slug || trialWorker;
        if (!slug) {
            toast.error("Pick a Workforce Member first, then try again.");
            close();
            nav("/app/workforce");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/access/start-trial", {
                worker_slug: slug,
                attribution: {
                    source: "paywall_modal",
                    feature_gate: feature,
                    upgrade_sku_hint: upgradeSku,
                    referrer: window.location.pathname,
                },
            });
            toast.success(`Trial started — you have ${data.trial_days_remaining} days.`);
            // Notify the whole app so widgets refresh.
            window.dispatchEvent(new CustomEvent("wfos:access-changed", { detail: data }));
            close();
            if (data.hired_id) nav(`/app/workers/${data.hired_id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start the trial. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const seePricing = () => {
        close();
        nav(`/app/billing?highlight=${encodeURIComponent(upgradeSku)}&source=paywall_modal&feature=${encodeURIComponent(feature)}`);
    };

    return (
        <div
            className="fixed inset-0 z-[100] grid place-items-center p-4"
            style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
            data-testid="paywall-modal"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="atlas-card w-full max-w-lg p-6 relative"
                style={{
                    borderColor: "rgba(139,92,246,0.55)",
                    boxShadow: "0 30px 90px rgba(139,92,246,0.25), var(--atlas-shadow-card-hi)",
                }}
            >
                <button
                    onClick={close}
                    data-testid="paywall-close-btn"
                    className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center hover:opacity-80"
                    style={{ background: "var(--atlas-bg-highlight)", color: "var(--atlas-text-muted)" }}
                >
                    <X className="w-4 h-4" strokeWidth={2} />
                </button>

                <div className="flex items-start gap-3">
                    <div
                        className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0"
                        style={{
                            background: "var(--atlas-primary-muted)",
                            border: "1px solid rgba(139,92,246,0.45)",
                        }}
                    >
                        {trialForHire ? (
                            <Sparkles className="w-5 h-5" strokeWidth={1.75} style={{ color: "var(--atlas-primary-hover)" }} />
                        ) : (
                            <Lock className="w-5 h-5" strokeWidth={1.75} style={{ color: "var(--atlas-primary-hover)" }} />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="atlas-label" data-testid="paywall-feature-label">{featureLabel}</div>
                        <h3
                            className="mt-1 text-xl font-semibold leading-tight"
                            style={{ color: "var(--atlas-text-primary)" }}
                            data-testid="paywall-headline"
                        >
                            {headline}
                        </h3>
                        <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "var(--atlas-text-tertiary)" }} data-testid="paywall-message">
                            {message}
                        </p>
                    </div>
                </div>

                {/* Usage / cap detail */}
                {(detail.current != null && detail.cap != null && detail.cap > 0) && (
                    <div className="mt-4 atlas-card p-3" style={{ background: "var(--atlas-bg-highlight)" }} data-testid="paywall-usage-bar">
                        <div className="flex items-center justify-between text-[11.5px]">
                            <span style={{ color: "var(--atlas-text-muted)" }}>Usage this month</span>
                            <span style={{ color: "var(--atlas-text-secondary)" }} className="tabular-nums font-medium">
                                {detail.current} / {detail.cap}
                            </span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--atlas-bg-elevated)" }}>
                            <div
                                className="h-full"
                                style={{
                                    width: `${Math.min(100, (detail.current / Math.max(1, detail.cap)) * 100)}%`,
                                    background: "var(--atlas-gradient-btn)",
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Trial-active status */}
                {detail.trial_days_remaining != null && detail.trial_days_remaining > 0 && (
                    <div className="mt-4 flex items-center gap-2 text-[13px] atlas-card p-3"
                         style={{ borderColor: "rgba(16,185,129,0.45)", background: "var(--atlas-success-muted)" }}>
                        <Clock className="w-4 h-4" strokeWidth={1.75} style={{ color: "#34d399" }} />
                        <span style={{ color: "#34d399" }}>
                            <b>{detail.trial_days_remaining} days left</b> in your free trial.
                        </span>
                    </div>
                )}

                {/* CTAs */}
                <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    {trialForHire && (
                        <button
                            onClick={startTrial}
                            disabled={busy}
                            data-testid="paywall-start-trial-btn"
                            className="atlas-btn-primary flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg h-11 text-[13.5px] font-semibold disabled:opacity-60"
                        >
                            <Sparkles className="w-4 h-4" strokeWidth={2} />
                            {busy ? "Starting…" : "Start free 14-day trial"}
                        </button>
                    )}
                    <button
                        onClick={seePricing}
                        data-testid="paywall-see-pricing-btn"
                        className={`${trialForHire ? "atlas-btn-ghost" : "atlas-btn-primary"} flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg h-11 text-[13.5px] font-semibold`}
                    >
                        See pricing plans <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                </div>

                {trialForHire && (
                    <p className="mt-3 text-[11px] text-center" style={{ color: "var(--atlas-text-muted)" }}>
                        Trial covers one Workforce Member for 14 days. No credit card required.
                    </p>
                )}
            </div>
        </div>
    );
}
